import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { CollapseProps, Collapse as AntdCollapse, Flex } from 'antd'
import { useMemo, useState } from 'react'

import {
  useInstitutionServiceDeleteInstitutionDepartmentEndpoint,
  useInstitutionServiceGetInstitutionDepartmentListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetInstitutionDepartmentListResponse,
  SystemLanguage
} from 'src/api/requests'
import {
  TableActionButtons,
  CollapseExpandArrow,
  Text,
  DeleteConfirm
} from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { AddDepartmentModal } from '../AddDepartmentModal'

import { useStyles } from './styles'

type Props = {
  id?: string
  data: GetInstitutionDepartmentListResponse
}

export const Collapse = ({ id: institutionId, data }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const { styles } = useStyles()
  const { t, currentLang } = useLocales()

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>()

  const onEditCLick = (id?: string) => {
    setSelectedDepartmentId(id)
    setIsModalOpen(true)
  }

  const onCancel = () => {
    setSelectedDepartmentId(undefined)
    setIsModalOpen(false)
  }

  const { mutate } = useInstitutionServiceDeleteInstitutionDepartmentEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useInstitutionServiceGetInstitutionDepartmentListEndpointKey]
      })
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const getTranslation = (count?: number) => {
    if (!count) return
    if (count === 1) return t('common.doctor')
    if (count > 4) return t('common.doctors')

    return t('common.doctors_2_to_4')
  }

  const getItems = useMemo(
    () => (id?: string) => {
      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => onEditCLick(id)
        },
        {
          key: 'delete',
          label: (
            <DeleteConfirm
              title={t('common.delete')}
              onConfirm={() =>
                mutate({ id: String(institutionId), departmentId: String(id) })
              }
            >
              <span>{t('common.delete')}</span>
            </DeleteConfirm>
          ),
          danger: true
        }
      ]
    },
    [t]
  )

  const collapseItems: CollapseProps['items'] = data?.data?.map(
    ({ departmentGroup, departments }) => ({
      key: departmentGroup?.id,
      label: (
        <Flex gap={24} align="center">
          <Text size="xl" strong>
            {currentLang?.value === SystemLanguage.RO
              ? departmentGroup?.translation?.ro
              : departmentGroup?.translation?.ru}
          </Text>
        </Flex>
      ),
      children: (
        <Flex vertical className={styles.collapseWrapper}>
          {departments?.map((child) => (
            <Flex key={child.id} align="center">
              <Flex className={styles.collapseRow}>
                <Text ellipsis>{child.name}</Text>
              </Flex>
              <Flex className={styles.collapseRow}>
                {child.userCount} {getTranslation(child.userCount)}
              </Flex>
              <Flex className={styles.actions}>
                <TableActionButtons items={getItems(child?.id)} />
              </Flex>
            </Flex>
          ))}
        </Flex>
      ),
      className: styles.collapseMarginBottom
    })
  )

  return (
    <>
      <AntdCollapse
        expandIconPosition="end"
        bordered={false}
        expandIcon={CollapseExpandArrow}
        items={collapseItems}
      />
      {isModalOpen && (
        <AddDepartmentModal
          institutionId={institutionId}
          id={selectedDepartmentId}
          onCancel={onCancel}
        />
      )}
    </>
  )
}
